// =============================================================================
// ENDING SCREEN - Фінальний екран (Tactical HUD)
// =============================================================================

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { trackGameComplete, trackFeedbackModalOpen, trackFeedbackModalDismiss } from '../services/analytics';
import { hasSubmittedFeedback } from '../services/feedbackService';
import { ShareButton } from './sharing/ShareButton';
import { ShareCardModal } from './sharing/ShareCardModal';
import { ShareStoriesModal } from './sharing/ShareStoriesModal';
import { ShareStoriesButton } from './sharing/ShareStoriesButton';
import { FeedbackModal } from './FeedbackModal';
import type { GameStateData } from '../services/storiesCardGenerator';

interface EndingScreenProps {
  militaryStrength: number;
  economy: number;
  resilience: number;
  westernSupport: number;
  trust: number;
  endingSummary: string;
  totalTimeSeconds: number;
  onRestart: () => void;
}

function getGameResult(average: number) {
  if (average >= 75) {
    return {
      title: 'СТРАТЕГІЧНА ПЕРЕВАГА',
      status: 'SUCCESS',
      color: 'text-war-primary',
    };
  } else if (average >= 50) {
    return {
      title: 'ТУПИКОВА СИТУАЦІЯ',
      status: 'STALEMATE',
      color: 'text-war-amber',
    };
  } else {
    return {
      title: 'ОПЕРАЦІЙНИЙ ПРОВАЛ',
      status: 'CRITICAL FAILURE',
      color: 'text-war-red',
    };
  }
}

export function EndingScreen({
  militaryStrength,
  economy,
  resilience,
  westernSupport,
  trust,
  endingSummary,
  totalTimeSeconds,
  onRestart,
}: EndingScreenProps) {
  const average =
    (militaryStrength + economy + resilience + westernSupport + trust) / 5;
  const result = getGameResult(average);

  // Track game completion on mount
  useEffect(() => {
    trackGameComplete({
      finalStats: {
        militaryStrength,
        economy,
        resilience,
        westernSupport,
        trust,
      },
      totalTimeSeconds,
    });
  }, []);

  // Share functionality
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [storiesModalOpen, setStoriesModalOpen] = useState(false);

  // Feedback functionality
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [showFeedbackButton, setShowFeedbackButton] = useState(true);

  // Determine game result for feedback
  const gameResult = average >= 75 ? 'victory' : average >= 50 ? 'stalemate' : 'defeat';

  // Check if user already submitted feedback
  useEffect(() => {
    if (hasSubmittedFeedback()) {
      setShowFeedbackButton(false);
    }
  }, []);

  const handleFeedbackOpen = () => {
    trackFeedbackModalOpen(gameResult);
    setFeedbackModalOpen(true);
  };

  const handleFeedbackClose = () => {
    if (!feedbackModalOpen) return;
    trackFeedbackModalDismiss(gameResult);
    setFeedbackModalOpen(false);
    // Приховати кнопку після закриття модалу
    setShowFeedbackButton(false);
  };

  // Stories data
  const storiesGameState: GameStateData = {
    turn: 20, // Final turn
    maxTurns: 20,
    date: new Date().toLocaleDateString('uk-UA'),
    location: 'Бункер Генштабу, Київ',
    militaryStrength,
    economy,
    resilience,
    westernSupport,
    trust,
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 p-4 md:p-8 animate-fade-in">
      {/* Header Report */}
      <div className="border-4 border-double border-war-dim/30 p-6 md:p-10 bg-war-panel/20 relative">
        <div className="absolute top-4 right-6 text-[10px] font-mono opacity-30 uppercase">
          TOP SECRET // EYES ONLY
        </div>

        <div className="text-center space-y-4 mb-10">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 opacity-40">
              <img
                src="/logo.png"
                alt="Logo"
                className="w-full h-full object-contain filter drop-shadow-[0_0_8px_rgba(16,185,129,0.3)]"
              />
            </div>
          </div>
          <h1 className="text-war-primary font-mono text-xl md:text-3xl font-bold tracking-[0.5em] uppercase">
            ПІДСУМКОВИЙ ЗВІТ
          </h1>
          <p className="text-war-dim font-mono text-xs uppercase tracking-widest">
            Операція: "НЕЗЛАМНІСТЬ" // Результат зафіксовано
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Summary Section */}
          <div className="space-y-6">
            <div className="space-y-2">
              <span className="text-war-dim text-[10px] uppercase font-mono tracking-tighter">
                Статус операції:
              </span>
              <div
                className={`text-2xl font-mono font-bold ${result.color} border-l-4 border-current pl-4 py-1`}
              >
                {result.title}
              </div>
            </div>

            <div className="space-y-2">
              <span className="text-war-dim text-[10px] uppercase font-mono tracking-tighter">
                Наративний підсумок:
              </span>
              <div className="bg-war-bg/40 p-5 border border-war-dim/10 text-war-bright font-mono text-sm leading-relaxed whitespace-pre-wrap">
                {endingSummary}
                <motion.span
                  animate={{ opacity: [1, 0] }}
                  transition={{ repeat: Infinity, duration: 1 }}
                  className="inline-block w-2 h-4 bg-war-primary ml-1"
                />
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="space-y-6 bg-war-panel/40 p-6 border border-war-dim/20">
            <h3 className="text-war-primary font-mono text-xs font-bold uppercase tracking-widest mb-4 border-b border-war-dim/20 pb-2">
              Метрики ефективності:
            </h3>

            <div className="space-y-4">
              {[
                { label: 'Сила ЗС', val: militaryStrength },
                { label: 'Економіка', val: economy },
                { label: 'Стійкість', val: resilience },
                { label: 'Підтримка', val: westernSupport },
                { label: 'Довіра', val: trust },
              ].map((s) => (
                <div
                  key={s.label}
                  className="flex justify-between items-end border-b border-war-dim/5 pb-1"
                >
                  <span className="text-[10px] text-war-dim font-mono uppercase">
                    {s.label}
                  </span>
                  <span className="text-sm font-mono text-war-bright font-bold">
                    {Math.round(s.val)}%
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-4 border-t border-war-dim/30">
              <div className="flex justify-between items-center bg-war-bg p-3">
                <span className="text-[10px] text-war-dim font-mono uppercase">
                  Загальний рейтинг:
                </span>
                <span className={`text-xl font-mono font-bold ${result.color}`}>
                  {Math.round(average)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-12 flex flex-col gap-4">
          {/* Stories Button - Primary */}
          <ShareStoriesButton
            onClick={() => setStoriesModalOpen(true)}
            variant="primary"
            className="w-full sm:w-auto sm:mx-auto"
          />

          {/* Secondary Buttons Row */}
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <ShareButton
              onClick={() => setShareModalOpen(true)}
              variant="secondary"
            />
            <button
              onClick={onRestart}
              className="btn-tactical text-sm px-10 py-4 font-bold"
            >
              ПОЧАТИ НОВИЙ ЦИКЛ ОПЕРАЦІЇ
            </button>
          </div>

          {/* Feedback Button */}
          {showFeedbackButton && (
            <button
              onClick={handleFeedbackOpen}
              className="mt-4 mx-auto flex items-center gap-2 text-war-dim hover:text-war-primary transition-colors font-mono text-xs uppercase tracking-wider group"
            >
              <svg
                className="w-4 h-4 group-hover:scale-110 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              Залишити відгук розробникам
            </button>
          )}
        </div>

        {/* Beta Version Message & Social Links */}
        <div className="mt-10 pt-6 border-t border-war-dim/20">
          <div className="text-center space-y-4">
            {/* Beta Badge */}
            <div className="inline-block px-3 py-1 bg-war-amber/10 border border-war-amber/30 text-war-amber font-mono text-[10px] uppercase tracking-widest">
              BETA v0.9
            </div>

            {/* Beta Message */}
            <p className="text-war-dim font-mono text-xs md:text-sm leading-relaxed max-w-md mx-auto">
              Ця гра ще у розробці. Якщо вам сподобалось — дайте нам знати через соцмережі!
              Ваш відгук допоможе зробити гру кращою.
            </p>

            {/* Social Links */}
            <div className="flex items-center justify-center gap-2 mt-4">
              <span className="text-[9px] text-war-dim/50 font-mono tracking-wider uppercase">
                Канали зв'язку:
              </span>
              <div className="flex items-center gap-1.5">
                <a
                  href="https://t.me/zavoruha_tech"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] border border-war-dim/30 text-war-dim hover:text-war-primary hover:border-war-primary/60 transition-colors"
                >
                  TG
                </a>
                <a
                  href="https://www.instagram.com/zavoruha"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] border border-war-dim/30 text-war-dim hover:text-war-primary hover:border-war-primary/60 transition-colors"
                >
                  IG
                </a>
                <a
                  href="https://www.threads.com/@zavoruha"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] border border-war-dim/30 text-war-dim hover:text-war-primary hover:border-war-primary/60 transition-colors"
                >
                  TR
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=61587632097637"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2 py-1 text-[10px] uppercase tracking-[0.18em] border border-war-dim/30 text-war-dim hover:text-war-primary hover:border-war-primary/60 transition-colors"
                >
                  FB
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center opacity-20 px-4">
        <span className="text-[8px] font-mono">CODE: BANNER_FALL_ZERO</span>
        <span className="text-[8px] font-mono uppercase">
          Authenticated by: STRATCOM_UA
        </span>
      </div>

      {/* Share Card Modal */}
      <ShareCardModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        stats={{ militaryStrength, economy, resilience, westernSupport, trust }}
      />

      {/* Instagram Stories Modal */}
      <ShareStoriesModal
        isOpen={storiesModalOpen}
        onClose={() => setStoriesModalOpen(false)}
        gameState={storiesGameState}
      />

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={handleFeedbackClose}
        gameResult={gameResult}
        averageScore={average}
      />
    </div>
  );
}
